import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { RippleModule } from 'primeng/ripple';

import {
  CellposeSegmenterService,
  VisualizationModule,
} from '@jax-data-science/sci-image-visualizer';

import { PipelineDialogComponent } from './pipeline-dialog.component';
import { PipelinePreviewComponent } from './pipeline-preview/pipeline-preview.component';
import { OpenCVEngine } from '../../models/processing-pipeline/engines/opencv/opencv-engine';
import { ClientCropEngine } from '../../models/processing-pipeline/engines/client-crop/client-crop-engine';
import { TransformersJSEngine } from '../../models/processing-pipeline/engines/transformers-js/transformers-engine';
import { SpeedyVisionEngine } from '../../models/processing-pipeline/engines/speedy-vision/speedy-vision-engine';
import { ServerEngine } from '../../models/processing-pipeline/engines/server/server-engine';
import { CellposeEngine } from '../../models/processing-pipeline/engines/cellpose/cellpose-engine';
import { ProcessService } from '../../../services/process.service';
import { MainFacade } from '../../main.facade';
import { PROCESSING_ENGINES } from '../../models/processing-pipeline/processing-engines.contract';
import { provideJaxPipelineEngines } from '../../../jax-engines.provider';

@NgModule({
  declarations: [PipelineDialogComponent, PipelinePreviewComponent],
  imports: [
    CommonModule,
    FormsModule,
    VisualizationModule,
    BadgeModule,
    ButtonModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    CheckboxModule,
    TableModule,
    TooltipModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    RippleModule,
  ],
  exports: [PipelineDialogComponent],
  providers: [
    // The open engines. Contributed through PROCESSING_ENGINES rather than
    // constructed in a module constructor, so a build configuration can vary the
    // set -- see that token. The JAX-only engines arrive from the swapped
    // provider below and are absent from a default build entirely.
    { provide: PROCESSING_ENGINES, multi: true, useFactory: () => new OpenCVEngine() },
    { provide: PROCESSING_ENGINES, multi: true, useFactory: () => new ClientCropEngine() },
    { provide: PROCESSING_ENGINES, multi: true, useFactory: () => new TransformersJSEngine() },
    { provide: PROCESSING_ENGINES, multi: true, useFactory: () => new SpeedyVisionEngine() },
    {
      provide: PROCESSING_ENGINES,
      multi: true,
      useFactory: (p: ProcessService, f: MainFacade) => new ServerEngine(p, f),
      deps: [ProcessService, MainFacade],
    },
    {
      provide: PROCESSING_ENGINES,
      multi: true,
      // Shares the library's cellpose-SAM instance with the toolbar Cellpose
      // tool. Open on purpose: cellpose and SAM are what the public demo runs.
      useFactory: (c: CellposeSegmenterService) => new CellposeEngine(c),
      deps: [CellposeSegmenterService],
    },
    ...provideJaxPipelineEngines(),
  ],
})
export class ProcessingPipelineModule {}
